import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";

import { toasts } from "../components/AppToast";
import { FiCheckCircle, FiGlobe, FiLayers } from "react-icons/fi";

export default function DynamicMessages() {
  const [sideOpen, setSideOpen] = useState(true);
  const [activePage, setActivePage] = useState("Messages");
  const [user, setUser] = useState(null);

  const [langid, setLangid] = useState("");
  const [langname, setLangname] = useState("");

  const [languages, setLanguages] = useState([]);

  const [messages, setMessages] = useState([]);
  const [databaseMessages, setDatabaseMessages] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Total count for the badge banner
  const totalCount = messages.length + databaseMessages.length;

  // AUTH EFFECT

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }
    setUser(JSON.parse(localStorage.getItem("user") || "null"));
  }, []);

  // GET DATA

  const getMessages = async (selectedLanguage = "") => {
    try {
      setLoading(true);

      const params = {};

      if (selectedLanguage) {
        params.langid = selectedLanguage;
      }

      const response = await API.get("/manage-lanaguage/dynamic-messages", {
        params,
      });

      if (!response.data?.success) {
        toasts.error(response.data?.message || "Failed to load messages");
        return;
      }

      const data = response.data;

      setLangid(String(data.langid || ""));
      setLangname(data.langname || "");

      setLanguages(data.languages || []);

      setMessages(data.messages || []);

      setDatabaseMessages(data.databaseMessages || []);
    } catch (error) {
      console.error("Get Dynamic Messages Error:", error);

      toasts.error(error.response?.data?.message || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  // INITIAL LOAD

  useEffect(() => {
    getMessages();
  }, []);

  // LANGUAGE CHANGE

  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;

    setLangid(selectedLanguage);

    if (!selectedLanguage) {
      setLangname("");
      setMessages([]);
      setDatabaseMessages([]);
      return;
    }

    getMessages(selectedLanguage);
  };

  // CHANGE NORMAL MESSAGE

  const handleMessageChange = (index, value) => {
    setMessages((previousMessages) =>
      previousMessages.map((message, messageIndex) =>
        messageIndex !== index
          ? message
          : {
              ...message,
              translatedValue: value,
            }
      )
    );
  };

  // CHANGE DATABASE MESSAGE

  const handleDatabaseMessageChange = (index, value) => {
    setDatabaseMessages((previousMessages) =>
      previousMessages.map((message, messageIndex) =>
        messageIndex !== index
          ? message
          : {
              ...message,
              translatedValue: value,
            }
      )
    );
  };

  // SUBMIT

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!langid) {
      toasts.warning("Language is required");
      return;
    }

    if (!messages.length && !databaseMessages.length) {
      toasts.warning("No messages available");
      return;
    }

    // Validate normal messages
    for (const message of messages) {
      if (!String(message.translatedValue || "").trim()) {
        toasts.warning(`Translation is required for "${message.englishValue}"`);
        return;
      }
    }

    // Validate database messages
    for (const message of databaseMessages) {
      if (!String(message.translatedValue || "").trim()) {
        toasts.warning(`Translation is required for "${message.englishValue}"`);
        return;
      }
    }

    try {
      setSaving(true);

      const payload = {
        langid,

        messages: messages.map((message) => ({
          id: message.id,
          value: message.translatedValue || "",
        })),

        databaseMessages: databaseMessages.map((message) => ({
          id: message.id,
          value: message.translatedValue || "",
        })),
      };

      const response = await API.post(
        "/manage-lanaguage/dynamic-messages",
        payload
      );

      if (response.data?.success) {
        toasts.success(response.data.message || "Messages saved successfully");

        await getMessages(langid);
      } else {
        toasts.error(response.data?.message || "Failed to save messages");
      }
    } catch (error) {
      console.error("Save Dynamic Messages Error:", error);

      toasts.error(error.response?.data?.message || "Failed to save messages");
    } finally {
      setSaving(false);
    }
  };

  // CLEAR

  // const handleClear = () => {
  //   setMessages((previousMessages) =>
  //     previousMessages.map((message) => ({
  //       ...message,
  //       translatedValue: "",
  //     }))
  //   );

  //   setDatabaseMessages(
  //     (previousMessages) =>
  //       previousMessages.map((message) => ({
  //         ...message,
  //         translatedValue: "",
  //       }))
  //   );
  // };

  return (
    <Layout
      sideOpen={sideOpen}
      setSideOpen={setSideOpen}
      activePage={activePage}
      setActivePage={setActivePage}
      user={user}
    >
      <main className="dashboard-main">
        <div className="user-form-page">
          {/* HEADER */}
          <div className="user-form-header">
            <div className="d-flex align-items-center gap-2">
              <div
                className="form-card-icon"
                style={{
                  color: "#0C807C",
                  background: "rgba(12, 128, 124, 0.1)",
                }}
              >
                <FiGlobe size={24} color="#0C807C" />
              </div>
              <div>
                <h2 className="um-modal-title mb-0">Messages</h2>
                <p className="form-card-sub mb-0">
                  (Manage multilingual interface and validation messages)
                </p>
              </div>
            </div>
          </div>

          {/* FORM CARD */}
          <div className="user-form-card">
            <form id="frmmlinfos" onSubmit={handleSubmit} autoComplete="off">
              {/* CONFIGURATION SETTINGS */}
              <div className="sec-div">
                <span>Configuration Settings</span>
              </div>

              <div className="row g-4">
                {/* LANGUAGE */}
                <div className="col-xl-6 col-lg-6 col-md-6">
                  <label htmlFor="cmbLanguage" className="um-label">
                    Language <span className="text-danger">*</span>
                  </label>

                  <select
                    name="cmbLanguage"
                    id="cmbLanguage"
                    className="um-input"
                    value={langid}
                    onChange={handleLanguageChange}
                    disabled={loading || saving}
                  >
                    <option value="">-- Select Language --</option>

                    {languages.map((language) => (
                      <option key={language.lang_id} value={language.lang_id}>
                        {language.lang_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* TRANSLATION SECTION */}
              <div className="sec-div mt-4">
                <span>Translate From English To {langname || "Language"}</span>
              </div>

              <div className="text-muted small mb-3">
                Enter the translation equivalents for messages below.
              </div>

              {/* LOADING */}
              {loading && (
                <div className="text-center py-5">
                  <span className="spinner" />
                  <div className="mt-2 text-muted">Loading messages...</div>
                </div>
              )}

              {/* NO RECORDS */}
              {!loading &&
                messages.length === 0 &&
                databaseMessages.length === 0 && (
                  <div className="alert alert-info">No messages found.</div>
                )}

              {/* MESSAGES LIST CONTENT */}
              {!loading &&
                (messages.length > 0 || databaseMessages.length > 0) && (
                  <>
                    <div
                      className="d-flex align-items-center justify-content-between mb-3 p-3 rounded border"
                      style={{
                        backgroundColor: "rgba(12, 128, 124, 0.04)",
                        borderColor: "rgba(12, 128, 124, 0.2)",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <FiLayers size={18} style={{ color: "#0C807C" }} />
                        <span className="fw-bold" style={{ color: "#0C807C" }}>
                          Total Messages Records
                        </span>
                      </div>
                      <span
                        className="badge px-3 py-2 rounded-pill shadow-sm"
                        style={{
                          backgroundColor: "#0C807C",
                          color: "#ffffff",
                          fontSize: "0.85rem",
                          fontWeight: "600",
                        }}
                      >
                        {totalCount} {totalCount === 1 ? "Item" : "Items"}
                      </span>
                    </div>

                    {/* UNIFIED TABLE HEADER */}
                    <div className="sec-div mt-3 mb-3">
                      <div className="row w-100 m-0">
                        <div className="col-md-5">
                          <span>ENGLISH EQUIVALENT *</span>
                        </div>
                        <div className="col-md-7">
                          <span>
                            {(langname || "LANGUAGE").toUpperCase()} EQUIVALENT
                            *
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* NORMAL MESSAGES */}
                    {messages.length > 0 && (
                      <div className="mb-4">
                        <div className="alert alert-secondary py-2 mb-3">
                          <strong style={{ color: "rgb(12, 128, 124)" }}>
                            Validation / Interface Messages
                          </strong>
                        </div>

                        <div className="row g-4">
                          {messages.map((message, index) => (
                            <div
                              className="col-12"
                              key={`message-${message.id}`}
                            >
                              <div className="p-3 border rounded bg-light">
                                <div className="row g-3 align-items-center">
                                  <div className="col-md-5">
                                    <label
                                      className="um-label mb-0"
                                      style={{
                                        fontSize: "14px",
                                        color: "rgb(12, 128, 124)",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {message.englishValue}{" "}
                                      <span className="text-danger">*</span>
                                    </label>

                                    <input
                                      type="hidden"
                                      name="hidsecid[]"
                                      value={message.id}
                                    />

                                    <input
                                      type="hidden"
                                      name="hidfrmvaldsecid[]"
                                      value="19"
                                    />
                                  </div>

                                  <div className="col-md-7">
                                    <input
                                      type="text"
                                      name="txtsecval[]"
                                      className="um-input"
                                      maxLength="500"
                                      value={message.translatedValue || ""}
                                      onChange={(e) =>
                                        handleMessageChange(
                                          index,
                                          e.target.value
                                        )
                                      }
                                      disabled={saving}
                                      placeholder={`Enter ${
                                        langname || "translation"
                                      } equivalent`}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* DATABASE MESSAGES */}
                    {databaseMessages.length > 0 && (
                      <div className="mb-4">
                        {/* <div className="alert alert-secondary py-2 mb-3">
                          <strong style={{color: "rgb(12, 128, 124)"}}>Database Messages</strong>
                        </div> */}

                        <div className="row g-4">
                          {databaseMessages.map((message, index) => (
                            <div
                              className="col-12"
                              key={`db-message-${message.id}`}
                            >
                              <div className="p-3 border rounded bg-light">
                                <div className="row g-3 align-items-center">
                                  <div className="col-md-5">
                                    <label
                                      className="um-label mb-0"
                                      style={{
                                        fontSize: "14px",
                                        color: "rgb(12, 128, 124)",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {message.englishValue}{" "}
                                      <span className="text-danger">*</span>
                                    </label>

                                    <input
                                      type="hidden"
                                      name="hiddbmsgsecid[]"
                                      value={message.id}
                                    />

                                    <input
                                      type="hidden"
                                      name="hiddbvaldsecid[]"
                                      value="16"
                                    />
                                  </div>

                                  <div className="col-md-7">
                                    <input
                                      type="text"
                                      name="txtdbmsgsecval[]"
                                      className="um-input"
                                      maxLength="500"
                                      value={message.translatedValue || ""}
                                      onChange={(e) =>
                                        handleDatabaseMessageChange(
                                          index,
                                          e.target.value
                                        )
                                      }
                                      disabled={saving}
                                      placeholder={`Enter ${
                                        langname || "translation"
                                      } equivalent`}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

              {/* FORM FOOTER BUTTONS */}
              {!loading &&
                (messages.length > 0 || databaseMessages.length > 0) && (
                  <div className="user-form-footer mt-4">
                    {/* <button
                      type="button"
                      className="um-btn-secondary"
                      onClick={handleClear}
                      disabled={saving}
                    >
                      <FiX />
                      Clear
                    </button> */}

                    <button
                      type="submit"
                      className="um-btn-primary"
                      disabled={saving || loading}
                      style={{
                        backgroundColor: "#0C807C",
                        borderColor: "#0C807C",
                      }}
                    >
                      {saving ? (
                        <>
                          <span className="spinner" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiCheckCircle />
                          Submit Translations
                        </>
                      )}
                    </button>
                  </div>
                )}
            </form>
          </div>
        </div>
      </main>

   
    </Layout>
  );
}
